export const MAX_VISIBLE_QUEUE = 3;
export const QUEUE_ENTRY_SPACING = 0.7;

export interface QueueEntry {
  id: number;
}

export class EntranceQueue<T extends QueueEntry> {
  private readonly pendingEntries: T[] = [];
  private readonly visibleIds: number[] = [];
  private nextEntryAt = 0;

  enqueue(entry: T): void {
    this.pendingEntries.push(entry);
  }

  releaseNext(now: number, visibleCapacity = MAX_VISIBLE_QUEUE): T | undefined {
    const capacity = Math.max(0, Math.min(MAX_VISIBLE_QUEUE, visibleCapacity));
    if (this.visibleIds.length >= capacity || this.pendingEntries.length === 0 || now < this.nextEntryAt) return undefined;
    const entry = this.pendingEntries.shift()!;
    this.visibleIds.push(entry.id);
    this.nextEntryAt = now + QUEUE_ENTRY_SPACING;
    return entry;
  }

  removeVisible(id: number): boolean {
    const index = this.visibleIds.indexOf(id);
    if (index < 0) return false;
    this.visibleIds.splice(index, 1);
    return true;
  }

  slotIndex(id: number): number {
    return this.visibleIds.indexOf(id);
  }

  get visible(): readonly number[] {
    return this.visibleIds;
  }

  get pending(): readonly T[] {
    return this.pendingEntries;
  }

  get hasOutstandingCustomers(): boolean {
    return this.pendingEntries.length > 0 || this.visibleIds.length > 0;
  }

  clear(): void {
    this.pendingEntries.length = 0;
    this.visibleIds.length = 0;
    this.nextEntryAt = 0;
  }
}
